import { useEffect, useState } from 'react';
import { 
  CheckCircle2, 
  Clock, 
  Filter,
  Search,
  X,
  Send,
  Loader2,
  History,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADOS = ['Nueva/Registrada', 'En Proceso', 'Completada'];
const PRIORIDADES = ['Baja', 'Media', 'Alta', 'Urgente'];

export default function Gestion() {
  const [incidencias, setIncidencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIncidencia, setSelectedIncidencia] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [traces, setTraces] = useState([]);
  const [loadingTraces, setLoadingTraces] = useState(false);
  const [showTraces, setShowTraces] = useState(false);
  
  const [filtros, setFiltros] = useState({
    estado: '',
    categoria: '',
    search: ''
  });
  
  const [updateForm, setUpdateForm] = useState({
    estado: '',
    Respuesta: '',
    prioridad: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    fetchIncidencias();
  }, [filtros]);

  const fetchIncidencias = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtros.estado) params.append('estado', filtros.estado);
      if (filtros.categoria) params.append('categoria', filtros.categoria);
      params.append('limit', '200');
      
      const response = await fetch(`${BACKEND_URL}/api/incidencias?${params.toString()}`);
      const data = await response.json();
      
      let features = data.features || [];
      
      if (filtros.search) {
        const searchLower = filtros.search.toLowerCase();
        features = features.filter(inc => {
          const attrs = inc.attributes;
          return (
            String(attrs.objectid).includes(searchLower) ||
            (attrs.Direccion || '').toLowerCase().includes(searchLower) ||
            (attrs.Observaciones || '').toLowerCase().includes(searchLower) ||
            (attrs.categoria || '').toLowerCase().includes(searchLower)
          );
        });
      }
      
      setIncidencias(features);
    } catch (error) {
      console.error('Error fetching incidencias:', error);
      toast.error('Error al cargar incidencias');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (incidencia) => {
    setSelectedIncidencia(incidencia);
    setUpdateForm({
      estado: incidencia.attributes.estado || '',
      Respuesta: incidencia.attributes.Respuesta || '',
      prioridad: incidencia.attributes.prioridad || 'Media'
    });
    setShowModal(true);
    await fetchTraces(incidencia.attributes.objectid);
  };

  const fetchTraces = async (objectid) => {
    setLoadingTraces(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/incidencias/${objectid}/traces`);
      const data = await response.json();
      setTraces(data.traces || []);
    } catch (error) {
      console.error('Error fetching traces:', error);
    } finally {
      setLoadingTraces(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedIncidencia(null);
    setTraces([]);
    setShowTraces(false);
    setUpdateForm({ estado: '', Respuesta: '', prioridad: '' });
  };

  const handleUpdate = async () => {
    if (!selectedIncidencia) return;
    
    if (!updateForm.estado) {
      toast.error('Selecciona un estado');
      return;
    }
    
    if (updateForm.estado !== 'Nueva/Registrada' && !updateForm.Respuesta) {
      toast.error('Debes agregar una respuesta para cambiar el estado');
      return;
    }
    
    setIsUpdating(true);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/incidencias/${selectedIncidencia.attributes.objectid}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updateForm)
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al actualizar');
      }
      
      toast.success('Incidencia actualizada correctamente');
      await fetchIncidencias();
      await fetchTraces(selectedIncidencia.attributes.objectid);
      
      setSelectedIncidencia(prev => ({
        ...prev,
        attributes: { ...prev.attributes, ...updateForm }
      }));
      
    } catch (error) {
      console.error('Error updating incidencia:', error);
      toast.error(error.message || 'Error al actualizar la incidencia');
    } finally {
      setIsUpdating(false);
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'Nueva/Registrada': return 'bg-red-100 text-red-800 border-red-200';
      case 'En Proceso': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Completada': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPrioridadColor = (prioridad) => {
    switch (prioridad) {
      case 'Urgente': return 'bg-red-100 text-red-800';
      case 'Alta': return 'bg-orange-100 text-orange-800';
      case 'Media': return 'bg-yellow-100 text-yellow-800';
      case 'Baja': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div data-testid="gestion-incidencias-page">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl border border-[#CFE3F5] p-6 mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Filter size={20} className="text-[#1E5AA8]" />
            <h2 className="font-display text-xl text-[#0A2540]">Filtros</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#0A2540] mb-2">
                <Search size={16} className="inline mr-2" />Buscar
              </label>
              <input
                type="text"
                value={filtros.search}
                onChange={(e) => setFiltros(prev => ({ ...prev, search: e.target.value }))}
                placeholder="ID, dirección, descripción..."
                className="w-full px-4 py-2.5 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#0A2540] mb-2">Estado</label>
              <select
                value={filtros.estado}
                onChange={(e) => setFiltros(prev => ({ ...prev, estado: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]"
              >
                <option value="">Todos</option>
                {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#0A2540] mb-2">Categoría</label>
              <select
                value={filtros.categoria}
                onChange={(e) => setFiltros(prev => ({ ...prev, categoria: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]"
              >
                <option value="">Todas</option>
                <option value="Limpieza Viaria">Limpieza Viaria</option>
                <option value="Recogida RSU">Recogida RSU</option>
                <option value="Playas y Litoral">Playas y Litoral</option>
                <option value="Parques y Jardines">Parques y Jardines</option>
                <option value="Otros servicios">Otros Servicios</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#CFE3F5] p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-xl text-[#0A2540]">Incidencias ({incidencias.length})</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={40} className="text-[#1E5AA8] animate-spin" />
            </div>
          ) : incidencias.length === 0 ? (
            <div className="text-center py-20 text-[#6F8AA8]">No se encontraron incidencias</div>
          ) : (
            <div className="space-y-3">
              {incidencias.map(inc => {
                const attrs = inc.attributes;
                const fecha = attrs.CreationDate ? new Date(attrs.CreationDate).toLocaleDateString('es-ES') : '-';
                
                return (
                  <div
                    key={attrs.objectid}
                    className="border border-[#CFE3F5] rounded-xl p-5 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => handleOpenModal(inc)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-lg font-semibold text-[#0A2540]">#{attrs.objectid}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getEstadoColor(attrs.estado)}`}>
                          {attrs.estado}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPrioridadColor(attrs.prioridad)}`}>
                          {attrs.prioridad || 'Media'}
                        </span>
                      </div>
                      <span className="text-sm text-[#6F8AA8]">{fecha}</span>
                    </div>
                    <p className="text-sm font-semibold text-[#0A2540] mb-2">
                      {attrs.categoria} - {attrs.tipo_incidencia || attrs.tipo_incidenciaRec || attrs.tipo_incidenciaPlayas || attrs.tipo_incidenciaJardines || 'Otro'}
                    </p>
                    <p className="text-sm text-[#6F8AA8] mb-2"><strong>Dirección:</strong> {attrs.Direccion}</p>
                    <p className="text-sm text-[#6F8AA8] line-clamp-2">{attrs.Observaciones}</p>
                    {attrs.Respuesta && (
                      <div className="mt-3 pt-3 border-t border-[#CFE3F5]">
                        <p className="text-xs font-semibold text-[#1F8A70] mb-1">Respuesta:</p>
                        <p className="text-sm text-[#6F8AA8] line-clamp-2">{attrs.Respuesta}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showModal && selectedIncidencia && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={handleCloseModal}>
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-[#CFE3F5] p-6 flex items-center justify-between rounded-t-3xl">
              <h2 className="font-display text-2xl text-[#0A2540]">Incidencia #{selectedIncidencia.attributes.objectid}</h2>
              <button onClick={handleCloseModal} className="w-10 h-10 rounded-full hover:bg-[#FAFCFE] flex items-center justify-center transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-[#F5F9FC] rounded-2xl p-6 space-y-3">
                <div className="flex gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getEstadoColor(selectedIncidencia.attributes.estado)}`}>
                    {selectedIncidencia.attributes.estado}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPrioridadColor(selectedIncidencia.attributes.prioridad)}`}>
                    {selectedIncidencia.attributes.prioridad || 'Media'}
                  </span>
                </div>
                <p className="text-sm text-[#6F8AA8]"><strong className="text-[#0A2540]">Categoría:</strong> {selectedIncidencia.attributes.categoria}</p>
                <p className="text-sm text-[#6F8AA8]"><strong className="text-[#0A2540]">Dirección:</strong> {selectedIncidencia.attributes.Direccion}</p>
                <p className="text-sm text-[#6F8AA8]"><strong className="text-[#0A2540]">Descripción:</strong> {selectedIncidencia.attributes.Observaciones}</p>
                {selectedIncidencia.attributes.email && (
                  <p className="text-sm text-[#6F8AA8]"><strong className="text-[#0A2540]">Email:</strong> {selectedIncidencia.attributes.email}</p>
                )}
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-[#0A2540]">Actualizar Incidencia</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#0A2540] mb-2">Estado <span className="text-red-500">*</span></label>
                    <select
                      value={updateForm.estado}
                      onChange={(e) => setUpdateForm(prev => ({ ...prev, estado: e.target.value }))}
                      className="w-full px-4 py-2.5 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]"
                    >
                      {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#0A2540] mb-2">Prioridad</label>
                    <select
                      value={updateForm.prioridad}
                      onChange={(e) => setUpdateForm(prev => ({ ...prev, prioridad: e.target.value }))}
                      className="w-full px-4 py-2.5 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]"
                    >
                      {PRIORIDADES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A2540] mb-2">
                    Respuesta {updateForm.estado !== 'Nueva/Registrada' && <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    value={updateForm.Respuesta}
                    onChange={(e) => setUpdateForm(prev => ({ ...prev, Respuesta: e.target.value }))}
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] resize-none"
                    placeholder="Escribe la respuesta para el ciudadano..."
                  />
                </div>
              </div>

              <div>
                <button
                  onClick={() => setShowTraces(!showTraces)}
                  className="flex items-center gap-2 text-[#1E5AA8] hover:text-[#0F4C97] transition-colors text-sm font-medium"
                >
                  <History size={18} />Historial de cambios ({traces.length})
                  {showTraces ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showTraces && (
                  <div className="mt-4 space-y-3">
                    {loadingTraces ? (
                      <div className="text-center py-4"><Loader2 size={24} className="text-[#1E5AA8] animate-spin mx-auto" /></div>
                    ) : traces.length === 0 ? (
                      <p className="text-sm text-[#6F8AA8] text-center py-4">No hay cambios registrados</p>
                    ) : (
                      traces.map(trace => (
                        <div key={trace.trace_id} className="bg-[#F5F9FC] rounded-xl p-4 border-l-4 border-[#1E5AA8]">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-mono text-[#6F8AA8]">{trace.trace_id}</span>
                            <span className="text-xs text-[#6F8AA8]">{new Date(trace.updated_at).toLocaleString('es-ES')}</span>
                          </div>
                          <ul className="text-sm text-[#6F8AA8] space-y-1 ml-4">
                            {trace.changes.estado && <li>• Estado: <strong>{trace.changes.estado}</strong></li>}
                            {trace.changes.Respuesta && <li>• Respuesta agregada</li>}
                          </ul>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={handleCloseModal}
                  className="flex-1 px-6 py-3 rounded-full border-2 border-[#CFE3F5] text-[#0A2540] font-medium hover:bg-[#FAFCFE] transition-colors"
                  disabled={isUpdating}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpdate}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#1F8A70] text-white font-medium hover:bg-[#16705B] transition-colors disabled:opacity-50"
                  disabled={isUpdating}
                >
                  {isUpdating ? (<><Loader2 size={18} className="animate-spin" />Actualizando...</>) : (<><Send size={18} />Guardar</>)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
